import React, { useState, useRef } from 'react';
import { message } from 'antd';
import { allowedFileExtensions, allowedImageExtensions } from '../configs/task.config';
import DragDropText from './DragDropText';

export const isFileAllowed = (file) => {
  const fileName = file.name.toLowerCase();
  const fileExtension = `.${fileName.split('.').pop()}`;

  if (file.type.startsWith('image/')) {
    return allowedImageExtensions.includes(fileExtension);
  } else {
    return allowedFileExtensions.includes(fileExtension);
  }
};

const FileDropPasteArea = ({ onImagesAdded, onFilesAdded, children }) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounter = useRef(0);
  const dropZoneRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(isFileAllowed);

    if (validFiles.length !== files.length) {
      message.error('Some files were rejected due to unsupported file types');
    }

    const droppedImages = [];
    const droppedFiles = [];

    validFiles.forEach((file) => {
      if (file.type.startsWith('image/')) {
        droppedImages.push(file);
      } else {
        droppedFiles.push(file);
      }
    });

    if (droppedImages.length > 0) {
      onImagesAdded && onImagesAdded(droppedImages);
    }
    if (droppedFiles.length > 0) {
      onFilesAdded && onFilesAdded(droppedFiles);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    let hasImage = false;
    const pastedImages = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        hasImage = true;
        const blob = item.getAsFile();
        const file = new File([blob], `pasted-image-${Date.now()}.${blob.type.split('/')[1]}`, {
          type: blob.type,
        });
        pastedImages.push(file);
      }
    }

    if (hasImage) {
      e.preventDefault();
      onImagesAdded && onImagesAdded(pastedImages);
    }
  };

  return (
    <div
      ref={dropZoneRef}
      onDrop={handleDrop}
      onPaste={handlePaste}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      className={`drop-zone ${isDraggingOver ? 'dragging-over' : ''}`}
    >
      {isDraggingOver && <DragDropText />}
      {children}
    </div>
  );
};

export default FileDropPasteArea;
